// PlayFirst Parent — Partner discount code entry.
// Reusable on the Pricing and Signup pages.
//
// Behaviour:
//   1. Parent types a code → click "Apply".
//   2. We POST to /api/parent/validate-partner-code.
//   3. On valid: stash {code, collaborator, redirect_url} in localStorage
//      (so the value survives the signup hop) and show success state.
//      If `redirectOnApply` is true, open the Paystack link.
//   4. On invalid: show the agreed copy.
//
// Storage keys: 'partner_code', 'partner_code_collaborator',
// 'partner_code_redirect_url'. ParentAppSignup reads these and calls
// /api/parent/apply-partner-code after the user is signed in.
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Tag, CheckCircle2, Loader2, ArrowRight, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'partner_code';

export function readStoredPartnerCode() {
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    if (!code) return null;
    return {
      code,
      collaborator: localStorage.getItem('partner_code_collaborator') || '',
      redirect_url: localStorage.getItem('partner_code_redirect_url') || '',
    };
  } catch {
    return null;
  }
}

export function clearStoredPartnerCode() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('partner_code_collaborator');
    localStorage.removeItem('partner_code_redirect_url');
  } catch {
    /* noop */
  }
}

export default function PartnerCodeEntry({
  redirectOnApply = false,   // Pricing page = true (sends to Paystack). Signup = false.
  className = '',
  testIdPrefix = 'partner-code',
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null); // {code, collaborator, redirect_url}
  const [error, setError] = useState('');

  // On mount, hydrate from localStorage if a code is already applied.
  useEffect(() => {
    const stored = readStoredPartnerCode();
    if (stored?.code) {
      setApplied(stored);
      setCode(stored.code);
    }
  }, []);

  const handleApply = async (e) => {
    e?.preventDefault?.();
    const trimmed = (code || '').trim();
    if (!trimmed) {
      setError('Enter a code first.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/parent/validate-partner-code`, { code: trimmed });
      if (r.data?.valid) {
        const payload = {
          code: r.data.code,
          collaborator: r.data.assigned_collaborator || '',
          redirect_url: r.data.redirect_url || '',
        };
        try {
          localStorage.setItem(STORAGE_KEY, payload.code);
          localStorage.setItem('partner_code_collaborator', payload.collaborator);
          localStorage.setItem('partner_code_redirect_url', payload.redirect_url);
        } catch { /* noop */ }
        setApplied(payload);
        setError('');
        if (redirectOnApply && payload.redirect_url) {
          // Brief pause so the success message is visible before the jump.
          setTimeout(() => {
            window.open(payload.redirect_url, '_blank', 'noopener,noreferrer');
          }, 700);
        }
      } else {
        setError(r.data?.message || 'That code does not seem to be active. Please check the spelling or continue without a code.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'That code does not seem to be active. Please check the spelling or continue without a code.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = () => {
    clearStoredPartnerCode();
    setApplied(null);
    setCode('');
    setError('');
  };

  if (applied) {
    return (
      <div
        className={`rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 ${className}`}
        data-testid={`${testIdPrefix}-applied`}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-900">
              Great news — your partner discount has been applied.
            </p>
            <p className="text-xs text-emerald-800 mt-1">
              You'll receive 30% off PlayFirst Parent.
            </p>
            <p className="text-xs text-emerald-700/80 mt-1.5">
              Code: <span className="font-semibold">{applied.code}</span>
              {applied.collaborator ? <span> · via {applied.collaborator}</span> : null}
            </p>
            {redirectOnApply && applied.redirect_url ? (
              <a
                href={applied.redirect_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-900 underline mt-2"
                data-testid={`${testIdPrefix}-checkout-link`}
              >
                Continue to checkout <ArrowRight className="w-3 h-3" />
              </a>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="text-emerald-700/70 hover:text-emerald-900"
            aria-label="Remove discount code"
            data-testid={`${testIdPrefix}-remove`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-stone-200 bg-white/70 backdrop-blur p-4 ${className}`}
      data-testid={`${testIdPrefix}-entry`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Tag className="w-4 h-4 text-[#7C6F9C]" />
        <p className="text-sm font-medium text-[#3a2f4f]">Have a partner discount code?</p>
      </div>
      <form onSubmit={handleApply} className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="e.g. BLOOM30"
          autoComplete="off"
          spellCheck={false}
          className="flex-1 h-11 px-4 rounded-xl border border-stone-200 bg-white text-sm tracking-wide uppercase placeholder:normal-case placeholder:text-stone-400 focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
          data-testid={`${testIdPrefix}-input`}
        />
        <button
          type="submit"
          disabled={busy}
          className="h-11 px-5 rounded-xl bg-[#7C6F9C] hover:bg-[#6a5d8a] text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid={`${testIdPrefix}-apply`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {busy ? 'Checking…' : 'Apply'}
        </button>
      </form>
      {error ? (
        <p
          className="text-xs text-rose-700 mt-2 leading-relaxed"
          data-testid={`${testIdPrefix}-error`}
        >
          {error}
        </p>
      ) : (
        <p className="text-xs text-stone-500 mt-2">
          Codes are case-insensitive. Discount applies at checkout.
        </p>
      )}
    </div>
  );
}
